// UK National Transmission System (NTS) Pipeline Network Data
// Approximate routes based on actual NTS infrastructure

export interface PipelineSegment {
  id: string;
  name: string;
  region: "scotland_north" | "west" | "east";
  pressureBar: number;
  coordinates: [number, number][];
}

export interface Node {
  id: string;
  name: string;
  type: "sensor" | "compressor" | "terminal" | "agi";
  status: "online" | "offline" | "degraded";
  coordinates: [number, number];
  batteryLevel: number;
  signalStrength: number;
  lastUpdate: string;
}

export interface Alert {
  id: string;
  type: "leak" | "pressure" | "intrusion" | "equipment" | "environmental";
  severity: "critical" | "high" | "medium" | "low";
  title: string;
  description: string;
  coordinates: [number, number];
  timestamp: string;
  affectedNodeId?: string;
  isActive: boolean;
}

// Main NTS Pipeline Routes (simplified representation)
export const pipelineSegments: PipelineSegment[] = [
  // Scotland & North
  {
    id: "sn-1",
    name: "Scotland Main",
    region: "scotland_north",
    pressureBar: 70,
    coordinates: [
      [-4.2518, 55.8642], // Glasgow
      [-3.9436, 55.9533], // Falkirk
      [-3.1883, 55.9533], // Edinburgh
      [-2.9916, 55.7558], // South of Edinburgh
      [-2.4074, 55.4907], // Borders
      [-1.6178, 54.9783], // Newcastle area
    ],
  },
  {
    id: "sn-2",
    name: "North East Feeder",
    region: "scotland_north",
    pressureBar: 70,
    coordinates: [
      [-1.6178, 54.9783], // Newcastle
      [-1.5491, 54.5748], // Durham
      [-1.2407, 54.2361], // Darlington
      [-1.0803, 53.9591], // York
      [-1.2407, 53.7997], // Leeds connection
    ],
  },
  {
    id: "sn-3",
    name: "St Fergus Terminal",
    region: "scotland_north",
    pressureBar: 85,
    coordinates: [
      [-1.8383, 57.5349], // St Fergus
      [-2.0986, 57.1497], // Aberdeen
      [-2.9687, 56.4620], // Dundee
      [-3.1883, 55.9533], // Edinburgh connection
    ],
  },
  // West Region
  {
    id: "w-1",
    name: "West Main",
    region: "west",
    pressureBar: 70,
    coordinates: [
      [-2.9916, 53.4084], // Liverpool
      [-2.7378, 53.4808], // Manchester
      [-2.4291, 53.5809], // East Manchester
      [-1.8983, 53.3811], // Sheffield
      [-1.2407, 53.7997], // Leeds
    ],
  },
  {
    id: "w-2",
    name: "Midlands Connector",
    region: "west",
    pressureBar: 70,
    coordinates: [
      [-2.7378, 53.4808], // Manchester
      [-2.1200, 52.9547], // Stoke
      [-1.8908, 52.4862], // Birmingham
      [-2.0986, 52.1929], // Worcester
      [-2.5879, 51.8642], // Gloucester
    ],
  },
  {
    id: "w-3",
    name: "South West Feeder",
    region: "west",
    pressureBar: 70,
    coordinates: [
      [-2.5879, 51.8642], // Gloucester
      [-2.5977, 51.4545], // Bristol
      [-3.1790, 51.4816], // Cardiff/Wales border
      [-3.9436, 51.6214], // South Wales
      [-4.1418, 51.8821], // Mid Wales
    ],
  },
  {
    id: "w-4",
    name: "Milford Haven",
    region: "west",
    pressureBar: 85,
    coordinates: [
      [-5.0540, 51.7075], // Milford Haven
      [-4.9707, 51.7520],
      [-4.1418, 51.8821], // Mid Wales connection
    ],
  },
  // East Region
  {
    id: "e-1",
    name: "East Main",
    region: "east",
    pressureBar: 70,
    coordinates: [
      [-1.2407, 53.7997], // Leeds
      [-1.0803, 53.9591], // York
      [-0.3462, 53.7456], // Hull
      [0.1210, 53.5679], // Grimsby
      [0.2747, 52.9547], // Lincoln
    ],
  },
  {
    id: "e-2",
    name: "Bacton Feeder",
    region: "east",
    pressureBar: 85,
    coordinates: [
      [1.4656, 52.8564], // Bacton Terminal
      [1.2979, 52.6309], // Norwich
      [0.4893, 52.2053], // Bury St Edmunds
      [0.1210, 52.0406], // Cambridge
      [-0.1257, 51.7520], // North London
    ],
  },
  {
    id: "e-3",
    name: "South East Connector",
    region: "east",
    pressureBar: 70,
    coordinates: [
      [-0.1257, 51.7520], // North London
      [-0.1257, 51.5074], // London
      [0.1789, 51.3891], // South East
      [0.5159, 51.2789], // Kent
      [1.0803, 51.2789], // East Kent
    ],
  },
  {
    id: "e-4",
    name: "Theddlethorpe Line",
    region: "east",
    pressureBar: 85,
    coordinates: [
      [0.2747, 53.4017], // Theddlethorpe
      [0.2747, 52.9547], // Connect to Lincoln
      [0.1210, 52.6309], // South
      [-0.1257, 52.2053], // Continue south
    ],
  },
  // Cross-country connectors
  {
    id: "cc-1",
    name: "Trans-Pennine",
    region: "west",
    pressureBar: 70,
    coordinates: [
      [-2.7378, 53.4808], // Manchester
      [-2.2388, 53.4808],
      [-1.8983, 53.3811], // Sheffield
      [-1.2407, 53.7997], // Leeds
    ],
  },
  {
    id: "cc-2",
    name: "Midlands Ring North",
    region: "east",
    pressureBar: 70,
    coordinates: [
      [-1.8908, 52.4862], // Birmingham
      [-1.4746, 52.4068], // Coventry
      [-1.1339, 52.6369], // Leicester
      [-0.7389, 52.9547], // Peterborough
      [0.1210, 52.6309], // East connection
    ],
  },
];

// Generate nodes along the pipelines
export const generateNodes = (): Node[] => {
  const nodes: Node[] = [];
  let nodeId = 1;

  // Compressor stations
  const compressorStations: Omit<Node, "id">[] = [
    { name: "St Fergus", type: "compressor", status: "online", coordinates: [-1.8383, 57.5349], batteryLevel: 100, signalStrength: 95, lastUpdate: new Date().toISOString() },
    { name: "Aberdeen", type: "compressor", status: "online", coordinates: [-2.0986, 57.1497], batteryLevel: 100, signalStrength: 92, lastUpdate: new Date().toISOString() },
    { name: "Kirriemuir", type: "compressor", status: "online", coordinates: [-3.0, 56.67], batteryLevel: 100, signalStrength: 88, lastUpdate: new Date().toISOString() },
    { name: "Moffat", type: "compressor", status: "online", coordinates: [-3.44, 55.33], batteryLevel: 100, signalStrength: 91, lastUpdate: new Date().toISOString() },
    { name: "Wooler", type: "compressor", status: "online", coordinates: [-2.01, 55.55], batteryLevel: 100, signalStrength: 89, lastUpdate: new Date().toISOString() },
    { name: "Bishop Auckland", type: "compressor", status: "online", coordinates: [-1.68, 54.66], batteryLevel: 100, signalStrength: 94, lastUpdate: new Date().toISOString() },
    { name: "Carnforth", type: "compressor", status: "degraded", coordinates: [-2.77, 54.13], batteryLevel: 100, signalStrength: 76, lastUpdate: new Date().toISOString() },
    { name: "Warrington", type: "compressor", status: "online", coordinates: [-2.59, 53.39], batteryLevel: 100, signalStrength: 97, lastUpdate: new Date().toISOString() },
    { name: "Alrewas", type: "compressor", status: "online", coordinates: [-1.75, 52.76], batteryLevel: 100, signalStrength: 93, lastUpdate: new Date().toISOString() },
    { name: "Churchover", type: "compressor", status: "online", coordinates: [-1.28, 52.41], batteryLevel: 100, signalStrength: 91, lastUpdate: new Date().toISOString() },
    { name: "Wormington", type: "compressor", status: "online", coordinates: [-1.95, 52.02], batteryLevel: 100, signalStrength: 90, lastUpdate: new Date().toISOString() },
    { name: "Peterborough", type: "compressor", status: "online", coordinates: [-0.24, 52.57], batteryLevel: 100, signalStrength: 95, lastUpdate: new Date().toISOString() },
    { name: "Bacton", type: "terminal", status: "online", coordinates: [1.4656, 52.8564], batteryLevel: 100, signalStrength: 98, lastUpdate: new Date().toISOString() },
    { name: "Theddlethorpe", type: "terminal", status: "online", coordinates: [0.2747, 53.4017], batteryLevel: 100, signalStrength: 96, lastUpdate: new Date().toISOString() },
    { name: "Milford Haven", type: "terminal", status: "online", coordinates: [-5.0540, 51.7075], batteryLevel: 100, signalStrength: 94, lastUpdate: new Date().toISOString() },
    { name: "Easington", type: "terminal", status: "online", coordinates: [-0.12, 53.65], batteryLevel: 100, signalStrength: 97, lastUpdate: new Date().toISOString() },
  ];

  // Add compressor stations
  compressorStations.forEach((station) => {
    nodes.push({
      id: `CS-${nodeId++}`,
      ...station,
    });
  });

  // Generate sensor nodes along pipelines
  pipelineSegments.forEach((segment) => {
    const coords = segment.coordinates;
    for (let i = 0; i < coords.length - 1; i++) {
      const start = coords[i];
      const end = coords[i + 1];

      // Calculate distance and add nodes every ~10km
      const distance = Math.sqrt(
        Math.pow(end[0] - start[0], 2) + Math.pow(end[1] - start[1], 2)
      );
      const numNodes = Math.max(2, Math.floor(distance * 20)); // Roughly 10km spacing

      for (let j = 1; j < numNodes; j++) {
        const t = j / numNodes;
        // Small offset to keep nodes close to pipeline (about 200m)
        const lng = start[0] + (end[0] - start[0]) * t + (Math.random() - 0.5) * 0.004;
        const lat = start[1] + (end[1] - start[1]) * t + (Math.random() - 0.5) * 0.004;

        // Random status with weighted probability
        const statusRand = Math.random();
        const status: Node["status"] = statusRand > 0.02 ? "online" : statusRand > 0.005 ? "degraded" : "offline";

        nodes.push({
          id: `N-${nodeId++}`,
          name: `Node ${segment.id.toUpperCase()}-${nodeId}`,
          type: "sensor",
          status,
          coordinates: [lng, lat],
          batteryLevel: Math.floor(Math.random() * 30) + 70,
          signalStrength: Math.floor(Math.random() * 25) + 75,
          lastUpdate: new Date(Date.now() - Math.random() * 3600000).toISOString(),
        });
      }
    }
  });

  return nodes;
};

// Convert to GeoJSON
export const getPipelineGeoJSON = (): GeoJSON.FeatureCollection => ({
  type: "FeatureCollection",
  features: pipelineSegments.map((segment) => ({
    type: "Feature" as const,
    properties: {
      id: segment.id,
      name: segment.name,
      region: segment.region,
      pressureBar: segment.pressureBar,
    },
    geometry: {
      type: "LineString" as const,
      coordinates: segment.coordinates,
    },
  })),
});

export const getNodesGeoJSON = (nodes: Node[]): GeoJSON.FeatureCollection => ({
  type: "FeatureCollection",
  features: nodes.map((node) => ({
    type: "Feature" as const,
    properties: {
      id: node.id,
      name: node.name,
      type: node.type,
      status: node.status,
      batteryLevel: node.batteryLevel,
      signalStrength: node.signalStrength,
    },
    geometry: {
      type: "Point" as const,
      coordinates: node.coordinates,
    },
  })),
});

// Sample alerts for demonstration
export const generateAlerts = (): Alert[] => [
  {
    id: "ALT-001",
    type: "leak",
    severity: "critical",
    title: "Potential Gas Leak Detected",
    description: "Abnormal pressure drop detected in pipeline segment. Immediate inspection required.",
    coordinates: [-2.7378, 53.4808], // Manchester area
    timestamp: new Date(Date.now() - 15 * 60000).toISOString(),
    isActive: true,
  },
  {
    id: "ALT-002",
    type: "pressure",
    severity: "high",
    title: "Pressure Anomaly",
    description: "Pressure readings outside normal parameters. Monitoring in progress.",
    coordinates: [-1.8983, 53.3811], // Sheffield
    timestamp: new Date(Date.now() - 45 * 60000).toISOString(),
    isActive: true,
  },
  {
    id: "ALT-003",
    type: "intrusion",
    severity: "high",
    title: "Unauthorized Activity Detected",
    description: "Motion sensors triggered near pipeline easement. Security team notified.",
    coordinates: [-0.1257, 51.5074], // London
    timestamp: new Date(Date.now() - 30 * 60000).toISOString(),
    isActive: true,
  },
  {
    id: "ALT-004",
    type: "equipment",
    severity: "medium",
    title: "Compressor Performance Degraded",
    description: "Carnforth compressor operating at reduced efficiency.",
    coordinates: [-2.77, 54.13], // Carnforth
    timestamp: new Date(Date.now() - 2 * 3600000).toISOString(),
    isActive: true,
  },
  {
    id: "ALT-005",
    type: "environmental",
    severity: "medium",
    title: "Ground Movement Detected",
    description: "Subsidence monitoring sensors triggered. Geological assessment scheduled.",
    coordinates: [-3.1883, 55.9533], // Edinburgh
    timestamp: new Date(Date.now() - 4 * 3600000).toISOString(),
    isActive: true,
  },
  {
    id: "ALT-006",
    type: "leak",
    severity: "low",
    title: "Minor Valve Seepage",
    description: "Small seepage detected at valve station. Scheduled for routine maintenance.",
    coordinates: [0.2747, 52.9547], // Lincoln area
    timestamp: new Date(Date.now() - 8 * 3600000).toISOString(),
    isActive: true,
  },
  {
    id: "ALT-007",
    type: "intrusion",
    severity: "critical",
    title: "Third Party Works Near Pipeline",
    description: "Excavation activity detected within 50m of pipeline. Emergency response initiated.",
    coordinates: [-1.5491, 54.5748], // Durham
    timestamp: new Date(Date.now() - 5 * 60000).toISOString(),
    isActive: true,
  },
];

export const getAlertsGeoJSON = (alerts: Alert[]): GeoJSON.FeatureCollection => ({
  type: "FeatureCollection",
  features: alerts
    .filter((alert) => alert.isActive)
    .map((alert) => ({
      type: "Feature" as const,
      properties: {
        id: alert.id,
        type: alert.type,
        severity: alert.severity,
        title: alert.title,
        description: alert.description,
        timestamp: alert.timestamp,
        affectedNodeId: alert.affectedNodeId,
      },
      geometry: {
        type: "Point" as const,
        coordinates: alert.coordinates,
      },
    })),
});
